import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { API_URL, BACKEND_URL } from '../config';
import { ErrorState } from '../components/ErrorState';
import { classifyError } from '../lib/apiError';
import {
  QrCode, Scan, Download, Share2, Copy, RefreshCw,
  ChevronRight, Zap, Check, X
} from "lucide-react";


export default function QRCodePage() {
  const [myQRCodes, setMyQRCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showGenerate, setShowGenerate] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [providerCode, setProviderCode] = useState("ESCOM");
  const [accountNumber, setAccountNumber] = useState("");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [processing, setProcessing] = useState(false);
  const [generatedQR, setGeneratedQR] = useState(null);
  const [scannedData, setScannedData] = useState(null);

  const token = localStorage.getItem("auth_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchQRCodes();
  }, []);

  const fetchQRCodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API_URL}/qr/my-codes`, { headers });
      setMyQRCodes(res.data);
    } catch (err) {
      console.error("Failed to load QR codes", err);
      setError(classifyError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePaymentQR = async (e) => {
    e.preventDefault();
    if (!accountNumber) {
      toast.error("Enter meter/account number");
      return;
    }
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/qr/generate/payment`, {
        provider_code: providerCode,
        account_number: accountNumber,
        amount: amount ? parseFloat(amount) : null,
        description
      }, { headers });
      setGeneratedQR(res.data);
      toast.success("QR Code generated!");
      fetchQRCodes();
    } catch (err) {
      toast.error(classifyError(err).message || "Failed to generate QR");
    } finally {
      setProcessing(false);
    }
  };

  const handleScanResult = async (qrData) => {
    try {
      const res = await axios.post(`${API_URL}/qr/scan`, {
        qr_data: qrData
      }, { headers });
      setScannedData(res.data);
      toast.success("QR Code scanned!");
    } catch (err) {
      toast.error(classifyError(err).message || "Invalid QR code");
    }
  };

  const copyQRData = (qr) => {
    const data = JSON.stringify({
      type: qr.type,
      provider_code: qr.provider_code,
      account_number: qr.account_number,
      amount: qr.amount
    });
    navigator.clipboard.writeText(data);
    toast.success("QR data copied!");
  };

  const downloadQR = (qrId) => {
    window.open(`${API_URL}/qr/image/${qrId}?size=400`, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={fetchQRCodes} />;
  }

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-6" data-testid="qr-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Share a pay code</h1>
        <div className="flex gap-2">
          <button
            onClick={() => setShowScanner(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
          >
            <Scan className="w-5 h-5" />
            Scan
          </button>
          <button
            onClick={() => setShowGenerate(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
            data-testid="generate-qr-btn"
          >
            <QrCode className="w-5 h-5" />
            Generate
          </button>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-2 gap-4">
        <button
          onClick={() => setShowGenerate(true)}
          className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          <div className="p-3 bg-indigo-100 dark:bg-indigo-900 rounded-full">
            <QrCode className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900 dark:text-white">Payment QR</p>
            <p className="text-sm text-gray-500">Generate for meter</p>
          </div>
        </button>
        <button
          onClick={() => setShowScanner(true)}
          className="flex items-center gap-4 p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
        >
          <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full">
            <Scan className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-left">
            <p className="font-semibold text-gray-900 dark:text-white">Scan QR</p>
            <p className="text-sm text-gray-500">Quick payment</p>
          </div>
        </button>
      </div>

      {/* My QR Codes */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">My QR Codes</h3>
        </div>
        
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {myQRCodes.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <QrCode className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No QR codes yet</p>
              <p className="text-sm">Generate one to share</p>
            </div>
          ) : (
            myQRCodes.slice(0, 10).map((qr) => (
              <div key={qr.id} className="flex items-center justify-between p-4">
                <div className="flex items-center gap-3">
                  <img
                    src={`${API_URL}/qr/image/${qr.id}?size=60`}
                    alt="QR Code"
                    className="w-12 h-12 rounded-lg bg-white"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      {qr.provider_code} - {qr.account_number}
                    </p>
                    <p className="text-xs text-gray-500">
                      {qr.amount ? `MWK ${qr.amount.toLocaleString()}` : "Any amount"}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => copyQRData(qr)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <Copy className="w-4 h-4 text-gray-500" />
                  </button>
                  <button
                    onClick={() => downloadQR(qr.id)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    <Download className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Generate Modal */}
      {showGenerate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <QrCode className="w-6 h-6 text-indigo-500" />
              Generate Payment QR
            </h3>
            
            {generatedQR ? (
              <div className="text-center space-y-4">
                <img
                  src={`${API_URL}/qr/image/${generatedQR.qr_id}?size=200`}
                  alt="Generated QR"
                  className="mx-auto rounded-xl bg-white p-2"
                />
                <p className="text-sm text-gray-500">Scan to pay for {providerCode} - {accountNumber}</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => downloadQR(generatedQR.qr_id)}
                    className="flex-1 py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Download
                  </button>
                  <button
                    onClick={() => { setGeneratedQR(null); setShowGenerate(false); }}
                    className="flex-1 py-3 rounded-xl bg-indigo-600 text-white"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleGeneratePaymentQR} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Provider</label>
                  <select
                    value={providerCode}
                    onChange={(e) => setProviderCode(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="ESCOM">ESCOM (Electricity)</option>
                    <option value="LWB">Lilongwe Water Board</option>
                    <option value="BWB">Blantyre Water Board</option>
                    <option value="DSTV">DStv</option>
                    <option value="GOTV">GOtv</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Meter/Account Number</label>
                  <input
                    type="text"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                    placeholder="Enter number"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    data-testid="qr-account-number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount (Optional)</label>
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Leave empty for any amount"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowGenerate(false)}
                    className="flex-1 py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={processing}
                    className="flex-1 py-3 rounded-xl bg-indigo-600 text-white disabled:opacity-50"
                    data-testid="confirm-generate-qr"
                  >
                    {processing ? "Generating..." : "Generate QR"}
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}

      {/* Scanner Modal */}
      {showScanner && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white dark:bg-gray-800 rounded-2xl p-6 w-full max-w-md"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Scan className="w-6 h-6 text-green-500" />
              Scan QR Code
            </h3>
            
            {scannedData ? (
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/30 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-700 dark:text-green-300">QR Scanned</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <p><span className="text-gray-500">Type:</span> {scannedData.type}</p>
                    {scannedData.provider_code && (
                      <p><span className="text-gray-500">Provider:</span> {scannedData.provider_code}</p>
                    )}
                    {scannedData.account_number && (
                      <p><span className="text-gray-500">Account:</span> {scannedData.account_number}</p>
                    )}
                    {scannedData.amount && (
                      <p><span className="text-gray-500">Amount:</span> MWK {scannedData.amount.toLocaleString()}</p>
                    )}
                    {scannedData.token && (
                      <p className="font-mono text-xs break-all"><span className="text-gray-500">Token:</span> {scannedData.token}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => { setScannedData(null); setShowScanner(false); }}
                    className="flex-1 py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                  >
                    Close
                  </button>
                  {scannedData.type === "payment" && (
                    <button
                      onClick={() => {
                        setShowScanner(false);
                        // Navigate to payment with pre-filled data
                        window.location.href = `/?provider=${scannedData.provider_code}&account=${scannedData.account_number}&amount=${scannedData.amount || ""}`;
                      }}
                      className="flex-1 py-3 rounded-xl bg-green-600 text-white"
                    >
                      Pay Now
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-xl flex items-center justify-center">
                  <div className="text-center">
                    <Scan className="w-16 h-16 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm text-gray-500">Camera scanner not available</p>
                    <p className="text-xs text-gray-400 mt-1">Paste QR data below</p>
                  </div>
                </div>
                <div>
                  <textarea
                    placeholder="Paste QR code data here"
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                    rows={3}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleScanResult(e.target.value);
                      }
                    }}
                  />
                </div>
                <button
                  onClick={() => setShowScanner(false)}
                  className="w-full py-3 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
}